import { Box, Button, FormControl, Typography } from '@mui/material'
import SelectInput from '../../../Common/SelectInput'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { updateLoader } from '../../../../redux/slices/loaderSlice'
import MultiSelectInput from '../../../Common/MultiSelectInput'
import SubTemplatesList from '../../../Common/SubTemplatesList'
import { fetchEntityInstanceData } from '../../../../services/entityInstanceServices'
import {
    updateAttributeValues,
    updateValuesForPayload,
} from '../../../../utils/EntityAttributesUtils'
import { store } from '../../../../redux/store'
import Fields from '../../../Common/Fields'
import { v4 as uuidv4 } from 'uuid'
import { AttributeType } from '../../../../constants/constants'
import { validateAttributes } from '../../../../utils/attributeValidations'
import { useTranslation } from 'react-i18next'

interface JsonTypeWithEntityProps {
    label: string
    id: string
    value: any
    onChange: any
    errorMessage?: string
    required?: boolean
    disabled?: boolean
    templateId: string
    isArray: boolean
    orgId: string
}

interface AttributeEntityValues {
    uid: string
    entityId: string
}

interface AddNewEntityDetails {
    templateId: string
    templateName: string
    entityId: string
    attributes: any[]
}

const JsonTypeWithEntity = (props: JsonTypeWithEntityProps): ReactElement => {
    const {
        label,
        id,
        value,
        onChange,
        errorMessage,
        required,
        disabled,
        templateId,
        isArray = false,
        orgId,
    } = props

    const org = useAppSelector((state) => state.org.org)
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const [recordsList, setRecordsList] = useState<any[]>([])

    const [selectedValues, setSelectedValues] = useState<
        AttributeEntityValues[]
    >([])

    //if it is entity json we can add new entity in inline
    const [openSubTemplates, setOpenSubTemplates] = useState(false)

    const [newEntityDetails, setNewEntityDetails] =
        useState<AddNewEntityDetails>()

    useEffect(() => {
        dispatch(updateLoader(true))
        if (templateId && value) {
            let getValue = Array.isArray(value) ? value : [value]
            getValue = getValue?.map((i: any) => {
                if (i.uid) {
                    return { ...i, entityId: uuidv4() }
                } else {
                    return { ...i, uid: i.id || i.Id || '', entityId: uuidv4() }
                }
            })

            setRecordsList(getValue)
            setSelectedValues(getValue)
        }
        dispatch(updateLoader(false))
    }, [templateId])

    useEffect(() => {
        if (newEntityDetails?.templateId) {
            onChange(id, '100%', 'width')
        } else {
            onChange(id, '', 'width')
        }
    }, [newEntityDetails])

    const onChangeValue = (array: any[]) => {
        let updatedValue = array
        if (!isArray) {
            updatedValue = array[0]
        }
        onChange(id, updatedValue)
    }

    const onChangeRecord = (id: string, entityIds: any) => {
        const updatedEntityIds = Array.isArray(entityIds)
            ? entityIds
            : Array(entityIds)

        const updatedEntityIdsData = recordsList
            .filter((item: any) => updatedEntityIds.includes(item?.uid))
            .map((item: any) => {
                const obj: any = {}

                for (const i in item) {
                    obj[i] = item[i]
                }
                return obj
            })
        setSelectedValues(updatedEntityIdsData)
        onChangeValue(updatedEntityIdsData)
    }

    const onClickAddNewInlineEntityJson = () => {
        setOpenSubTemplates(true)
    }

    const onCloseSubTemplatePopUp = () => {
        setOpenSubTemplates(false)
    }

    const onSubmitSubTemplatePopUp = (templateDetails: {
        templateId: string
        templateName: string
    }) => {
        getAndSetTheAddOrEditEntityDetails(templateDetails)
    }

    const getAndSetTheAddOrEditEntityDetails = async (templateDetails: {
        templateId: string
        templateName: string
        [key: string]: string
    }) => {
        dispatch(updateLoader(true))
        const EntityInstanceResponse: any = await fetchEntityInstanceData(
            orgId || org?.id || '',
            templateDetails?.templateId,
            '',
        )
        let updatedAttributesList = EntityInstanceResponse?.attributes?.map(
            (i: any) => {
                if (
                    i.dataType.toLowerCase() === AttributeType.TEMPERATURE ||
                    i.dataType.toLowerCase() === AttributeType.HUMIDITY ||
                    i.dataType.toLowerCase() === AttributeType.WEIGHT ||
                    i.dataType.toLowerCase() === AttributeType.DISTANCE ||
                    i.dataType.toLowerCase() === AttributeType.CURRENCY
                ) {
                    const updatedValue: any = templateDetails[i.id] || {}
                    return {
                        ...i,
                        unit: updatedValue?.units || i.unit,
                        value: updatedValue?.value || '',
                    }
                } else {
                    return { ...i, value: templateDetails[i.id] || '' }
                }
            },
        )

        updatedAttributesList = updateAttributeValues(
            updatedAttributesList || [],
            store,
            false,
        )

        const updateNewEntityDetails: AddNewEntityDetails = {
            templateId: templateDetails?.templateId,
            templateName: templateDetails?.templateName,
            entityId: templateDetails?.entityId || uuidv4(),
            attributes: updatedAttributesList,
        }
        setNewEntityDetails(updateNewEntityDetails)

        setOpenSubTemplates(false)
        dispatch(updateLoader(false))
    }

    const onClickRecordEdit = (rec: any) => {
        getAndSetTheAddOrEditEntityDetails(rec)
    }

    const onChangeNewEntityFields = (
        id: string,
        value: string,
        key?: string,
    ) => {
        const updateNewEntityDetails = {
            ...newEntityDetails,
            attributes: newEntityDetails?.attributes.map((i) => {
                if (i.id === id) {
                    const obj = { ...i }
                    obj[key ?? 'value'] = value
                    obj['errorMessage'] = ''
                    return obj
                } else {
                    return { ...i }
                }
            }),
        }
        setNewEntityDetails(updateNewEntityDetails as AddNewEntityDetails)
    }

    const onClickSaveNewEntity = () => {
        dispatch(updateLoader(true))
        const { isValidated, updatedFields } = validateAttributes(
            newEntityDetails?.attributes || [],
        )
        if (isValidated) {
            onSaveNewEntity()
        } else {
            setNewEntityDetails({
                ...newEntityDetails,
                attributes: updatedFields || [],
            } as AddNewEntityDetails)
            dispatch(updateLoader(false))
        }
    }

    const onSaveNewEntity = async () => {
        dispatch(updateLoader(true))
        const updatedAttibutes = await updateValuesForPayload(
            newEntityDetails?.attributes,
        )
        const UIdAttributes = updatedAttibutes.filter(
            (i: any) => i.metadata?.Displayable && i.value,
        )
        const UId = UIdAttributes.map((item: any) => {
            if (
                item.dataType.toLowerCase() === AttributeType.Entity_JSON ||
                item.dataType.toLowerCase() === AttributeType.ENTITY ||
                item.dataType.toLowerCase() === AttributeType.JSON
            ) {
                return item.value?.uid || ''
            } else {
                return item.value
            }
        })?.join(',')
        const addedRecordData: any = {
            uid: UId,
            entityId: newEntityDetails?.entityId,
            templateId: newEntityDetails?.templateId || '',
            templateName: newEntityDetails?.templateName || '',
        }

        updatedAttibutes?.forEach((i: any) => {
            if (i.dataType.toLowerCase() === AttributeType.ENTITY) {
                addedRecordData[i.id] = { ...i.value, dataType: i.dataType }
            } else {
                addedRecordData[i.id] = i.value
            }
        })
        let updatedValue = []
        if (isArray) {
            const index = selectedValues.findIndex(
                (i) => i.entityId === newEntityDetails?.entityId,
            )
            if (index !== -1) {
                updatedValue = selectedValues.map((i) => {
                    if (i.entityId === newEntityDetails?.entityId) {
                        return { ...addedRecordData }
                    } else {
                        return { ...i }
                    }
                })
            } else {
                updatedValue = [...selectedValues, addedRecordData]
            }
        } else {
            updatedValue = [addedRecordData]
        }
        setRecordsList(updatedValue)
        setSelectedValues(updatedValue)

        onChangeValue(updatedValue)
        setNewEntityDetails(undefined)

        dispatch(updateLoader(false))
    }

    return (
        <Box>
            <Box>
                <FormControl fullWidth disabled={!!disabled} size="small">
                    <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                        {label}
                        {required ? '*' : ''}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: ' 100%',
                                maxWidth: `${newEntityDetails ? 'calc(50% - 20px)' : '100%'}`,
                            }}
                        >
                            {isArray ? (
                                <MultiSelectInput
                                    label=""
                                    id={id}
                                    value={selectedValues?.map(
                                        ({ uid }) => uid || '',
                                    )}
                                    onChange={onChangeRecord}
                                    list={recordsList || []}
                                    inputProps={{ id: 'uid', name: 'uid' }}
                                    errorMessage={
                                        !selectedValues.length && errorMessage
                                            ? errorMessage
                                            : ''
                                    }
                                    disabled={disabled}
                                    hasAddNew={true}
                                    onClickAddNew={() =>
                                        onClickAddNewInlineEntityJson()
                                    }
                                    onClickRowIcon={(r: any) =>
                                        onClickRecordEdit(r)
                                    }
                                />
                            ) : (
                                <SelectInput
                                    label=""
                                    id={id}
                                    value={selectedValues[0]?.uid}
                                    onChange={onChangeRecord}
                                    list={recordsList}
                                    inputProps={{ id: 'uid', name: 'uid' }}
                                    errorMessage={
                                        !selectedValues.length && errorMessage
                                            ? errorMessage
                                            : ''
                                    }
                                    disabled={disabled}
                                    hasAddNew={true}
                                    onClickAddNew={() => {
                                        onClickAddNewInlineEntityJson()
                                    }}
                                    onClickRowIcon={(r: any) =>
                                        onClickRecordEdit(r)
                                    }
                                    allowClear={true}
                                />
                            )}
                        </Box>
                    </Box>
                </FormControl>
            </Box>
            {newEntityDetails && (
                <Box
                    className="childWidth100"
                    sx={{
                        background: '#00000010',
                        marginRight: '10px',
                        marginTop: '10px',
                        padding: '20px 0px 20px 20px',
                        borderRadius: '3px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ marginBottom: '15px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#000',
                            }}
                        >
                            {t('add')} {newEntityDetails?.templateName}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                    >
                        <Fields
                            fields={newEntityDetails?.attributes}
                            onChangeFields={onChangeNewEntityFields}
                            onClickMap={() => {}}
                            disabled={false}
                            orgId={orgId}
                        />
                    </Box>
                    <Box sx={{ marginLeft: 'auto', marginRight: '20px' }}>
                        <Button
                            onClick={() => {
                                setNewEntityDetails(undefined)
                            }}
                            sx={{
                                fontSize: '12px',
                                padding: '7px 20px',
                                marginRight: '15px',
                            }}
                            variant="outlined"
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => onClickSaveNewEntity()}
                            sx={{
                                width: '100px',
                                color: '#fff',
                                fontSize: '12px',
                                padding: '7px 20px',
                            }}
                        >
                            {t('save')}
                        </Button>
                    </Box>
                </Box>
            )}
            {openSubTemplates && (
                <SubTemplatesList
                    templateId={templateId}
                    isOpen={openSubTemplates}
                    onClose={onCloseSubTemplatePopUp}
                    onSubmit={onSubmitSubTemplatePopUp}
                    submitIfSingle={true}
                    orgId={orgId}
                />
            )}
        </Box>
    )
}

export default JsonTypeWithEntity
